import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Users, TrendingUp, MapPin, FileText, Moon, Sun, Calendar } from 'lucide-react';
import { useDarkMode } from './DarkModeContext';
import { usePoliticianData } from './PoliticianDataContext';

const fontStyle = document.createElement('style');
fontStyle.textContent = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
  body { font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; }
`;
document.head.appendChild(fontStyle);

const HomePage = () => {
  const navigate = useNavigate();
  const { isDarkMode, setIsDarkMode } = useDarkMode();
  const { politicians, politicianUpdates, availableTags, loading, error } = usePoliticianData();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedHomepageTag, setSelectedHomepageTag] = useState('all');
  const [selectedParty, setSelectedParty] = useState(null);
  const [showAllTags, setShowAllTags] = useState(false);

  const handleHomepageTagSelect = (tagName) => {
    setSelectedHomepageTag(tagName);
  };

  // Helper: Get latest update for a politician and tag
  const getLatestActivityForTag = (politicianId, tag) => {
    const updates = politicianUpdates[politicianId] || [];
    if (tag === 'all') {
      return updates[0] || null;
    }
    return updates.find(update => update.tags && Array.isArray(update.tags) && update.tags.includes(tag)) || null;
  };

  const filteredPoliticians = politicians.filter(politician => {
    if (!politician) return false;
    const matchesSearch =
      (politician.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (politician.office || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (politician.location || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (politician.party || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesParty = !selectedParty || politician.party === selectedParty;
    const matchesHomepageTag = selectedHomepageTag === 'all' || (politicianUpdates[politician.id] || []).some(update => update.tags && update.tags.includes(selectedHomepageTag));
    return matchesSearch && matchesParty && matchesHomepageTag;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-lg text-gray-600">Loading Georgia representatives and policy tags...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Users className="w-8 h-8 text-red-600" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Unable to Load Data</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button 
            onClick={() => window.location.reload()}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
      <header className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-sm`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <img src="/title.png" alt="CivicDuty Logo" className="h-10 w-auto cursor-pointer" style={{maxHeight: '2.5rem'}} onClick={() => navigate('/')} />
            </div>
            <div className="flex-1 max-w-md mx-8">
              <div className="relative">
                <Search className={`absolute left-4 top-1/2 transform -translate-y-1/2 ${isDarkMode ? 'text-gray-400' : 'text-gray-400'} w-5 h-5`} />
                <input
                  type="text"
                  placeholder="Search by name, office, or location..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className={`w-full pl-12 pr-6 py-3 text-base border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all shadow-sm ${
                    isDarkMode 
                      ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                      : 'bg-white border-gray-200 text-gray-900 placeholder-gray-500'
                  }`}
                />
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setIsDarkMode(!isDarkMode)}
                className={`p-2 rounded-lg transition-all duration-200 ${
                  isDarkMode 
                    ? 'bg-gray-700 text-yellow-400 hover:bg-gray-600' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
                aria-label="Toggle dark mode"
              >
                {isDarkMode ? (
                  <Sun className="w-5 h-5" />
                ) : (
                  <Moon className="w-5 h-5" />
                )}
              </button>
              <button
                onClick={() => navigate('/bills')}
                className={`flex items-center px-4 py-2 ${isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'} rounded-lg transition-all shadow-sm hover:shadow-md transform hover:scale-105`}
              >
                <FileText className="w-4 h-4 mr-2" />
                View Bills
              </button>
            </div>
          </div>
        </div>
      </header>
      <section className="py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-lg p-6 border ${isDarkMode ? 'border-gray-700' : 'border-gray-100'}`}>
            <div className="text-center mb-4">
              <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'} mb-2`}>
                Filter by Policy Topics
              </h3>
              <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                Select a topic to see representatives with recent activity in that area
              </p>
            </div>
            <div className="flex flex-wrap justify-center gap-2 max-h-32 overflow-y-auto custom-scrollbar">
              <button
                onClick={() => handleHomepageTagSelect('all')}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                  selectedHomepageTag === 'all'
                    ? 'bg-gradient-to-r from-red-600 to-blue-600 text-white shadow-md'
                    : isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
                }`}
              >
                All Topics
              </button>
              {availableTags.map(tag => (
                <button
                  key={tag.id || tag._id}
                  onClick={() => handleHomepageTagSelect(tag.name)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                    selectedHomepageTag === tag.name
                      ? 'bg-gradient-to-r from-red-600 to-blue-600 text-white shadow-md'
                      : isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
                  }`}
                >
                  {tag.name}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>
      <section className="py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {filteredPoliticians.length === 0 ? (
            <div className="text-center py-12">
              <div className={`w-16 h-16 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'} rounded-full flex items-center justify-center mx-auto mb-4`}>
                <Users className={`w-8 h-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
              </div>
              <h3 className={`text-lg font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-900'} mb-2`}>
                No representatives found
              </h3>
              <p className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>
                {selectedHomepageTag === 'all' 
                  ? 'No representatives match your search criteria.' 
                  : `No representatives have recent activity in "${selectedHomepageTag}".`
                }
              </p>
              <button
                onClick={() => {
                  setSelectedHomepageTag('all');
                  setSearchTerm('');
                }}
                className="mt-4 px-4 py-2 bg-gradient-to-r from-red-600 to-blue-600 text-white rounded-lg hover:from-red-700 hover:to-blue-700 transition-all"
              >
                Show All Representatives
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredPoliticians.map((politician) => {
                const latestActivity = getLatestActivityForTag(politician.id, selectedHomepageTag);
                const partyColor =
                  politician.party === 'Republican'
                    ? (isDarkMode ? 'text-red-400' : 'text-red-600')
                    : politician.party === 'Democrat'
                      ? (isDarkMode ? 'text-blue-400' : 'text-blue-600')
                      : (isDarkMode ? 'text-gray-300' : 'text-gray-600');
                const partyBg =
                  politician.party === 'Republican'
                    ? (isDarkMode ? 'bg-red-900 text-red-300' : 'bg-red-100 text-red-700')
                    : politician.party === 'Democrat'
                      ? (isDarkMode ? 'bg-blue-900 text-blue-300' : 'bg-blue-100 text-blue-700')
                      : (isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-800');
                const borderColor =
                  politician.party === 'Republican'
                    ? (isDarkMode ? 'border-red-800' : 'border-red-500')
                    : politician.party === 'Democrat'
                      ? (isDarkMode ? 'border-blue-800' : 'border-blue-500')
                      : (isDarkMode ? 'border-gray-700' : 'border-gray-300');
                return (
                  <div
                    key={politician.id}
                    onClick={() => navigate(`/profile/${politician.id}`)}
                    className={`group relative ${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1 cursor-pointer border-2 border-transparent ${
                      politician.party === 'Democrat'
                        ? isDarkMode ? 'hover:border-blue-800' : 'hover:border-blue-200'
                        : politician.party === 'Republican'
                        ? isDarkMode ? 'hover:border-red-800/50' : 'hover:border-red-200'
                        : isDarkMode ? 'hover:border-gray-600' : 'hover:border-gray-200'
                    }`}
                  >
                    <div className="p-6">
                      <div className="flex flex-col items-center text-center">
                        <img
                          src={politician.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(politician.name)}&background=random&color=fff`}
                          alt={politician.name}
                          className={`w-24 h-24 rounded-full object-cover border-4 mb-3 ${
                            politician.party === 'Republican'
                              ? 'border-red-500'
                              : politician.party === 'Democrat'
                                ? 'border-blue-500'
                                : 'border-gray-400'
                          }`}
                        />
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium mb-4 ${partyBg}`}>{politician.party}</span>
                        <h3 className={`text-lg font-semibold mb-2 ${partyColor}`}>{politician.name}</h3>
                        <p className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                          {politician.office}
                        </p>
                        <div className="flex items-center justify-center text-sm mb-4">
                          <MapPin className={`w-4 h-4 mr-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`} />
                          <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>
                            {politician.location}
                          </span>
                        </div>
                        {/* Latest Activity Card */}
                        <div className="w-full mt-4">
                          {latestActivity ? (
                            <div
                              className={`mt-4 p-4 rounded-lg border transition-colors 
                                ${politician.party === 'Republican'
                                  ? isDarkMode ? 'bg-gradient-to-br from-red-900/30 to-red-800/20 border-red-800 hover:from-red-900/50 hover:to-red-800/30' : 'bg-white border-red-200 hover:border-red-300'
                                  : politician.party === 'Democrat'
                                    ? isDarkMode ? 'bg-gradient-to-br from-blue-900/30 to-blue-800/20 border-blue-800 hover:from-blue-900/50 hover:to-blue-800/30' : 'bg-white border-blue-200 hover:border-blue-300'
                                    : isDarkMode ? 'bg-gray-700 border-gray-600 hover:bg-gray-600' : 'bg-white border-gray-200 hover:border-gray-300'
                                }`}
                            >
                              <div className={`flex items-center justify-between text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                                <div className="flex items-center">
                                  <TrendingUp className={`w-4 h-4 mr-2 ${
                                    politician.party === 'Republican'
                                      ? 'text-red-400'
                                      : politician.party === 'Democrat'
                                        ? 'text-blue-400'
                                        : 'text-gray-400'
                                  }`} />
                                  Latest Activity
                                </div>
                                <div className={`flex items-center text-xs ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                                  <Calendar className="w-3 h-3 mr-1" />
                                  <span>{latestActivity.formattedDate || latestActivity.date}</span>
                                </div>
                              </div>
                              <p className={`text-sm ${isDarkMode ? 'text-gray-400 line-clamp-3' : 'text-gray-900 line-clamp-3'}`}>{latestActivity.summary}</p>
                            </div>
                          ) : (
                            <div
                              className={`mt-4 p-4 rounded-lg border transition-colors 
                                ${politician.party === 'Republican'
                                  ? isDarkMode ? 'bg-gradient-to-br from-red-900/10 to-red-800/10 border-red-800 hover:from-red-900/50 hover:to-red-800/30' : 'bg-white border-red-200 hover:border-red-300'
                                  : politician.party === 'Democrat'
                                    ? isDarkMode ? 'bg-gradient-to-br from-blue-900/30 to-blue-800/20 border-blue-800 hover:from-blue-900/50 hover:to-blue-800/30' : 'bg-white border-blue-200 hover:border-blue-300'
                                    : isDarkMode ? 'bg-gray-700 border-gray-600 hover:bg-gray-600' : 'bg-white border-gray-200 hover:border-gray-300'
                                }`}
                            >
                              <div className={`flex items-center text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                                <TrendingUp className={`w-4 h-4 mr-2 ${
                                  politician.party === 'Republican'
                                    ? 'text-red-400'
                                    : politician.party === 'Democrat'
                                      ? 'text-blue-400'
                                      : 'text-gray-400'
                                }`} />
                                Latest Activity
                              </div>
                              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>No recent activity</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default HomePage; 